import {
  CompareOperator,
  DisplayFormatEnum,
  FormTemplateFieldAutopopulateConfig,
  TransformValueTypeEnum
} from 'src/app/shared/autopopulate/autopopulate.model';

/**
 * Intake form to patient non-modifiable data linking config.
 */
const medicalConditionKeys = [
  'abnormalBloodClotting',
  'accutaneTreatment',
  'acne',
  'androgenDeficiency',
  'anxiety',
  'arthritis',
  'asthma',
  'bellsPalsy',
  'boneMarrow',
  'cancer',
  'chronicOBstructivePulmonaryDisease',
  'chronicPainSyndrome',
  'coldSoresOrGentialHerpesOrShingles',
  'conditionOther',
  'coronaryArteryDisease',
  'dandruff',
  'demodexSkinInfection',
  'diabetes',
  'drugAllergies',
  'eczema',
  'environmentalAllergies',
  'epilepsy',
  'fertility',
  'fibromyalgia',
  'geneticConditions',
  'glaucoma',
  'gout',
  'graftHostDisease',
  'heartDisease',
  'heatIllness',
  'heaterDermatitis',
  'hepatitis',
  'hivOrAids',
  'htlv',
  'hypercholesterolemia',
  'hypertension',
  'irritableBowelSyndrome',
  'keloidScars',
  'liverDisease',
  'lungDisease',
  'lupus',
  'migraine',
  'osteoporosis',
  'otherSkinConditions',
  'pacemaker',
  'pelvicPain',
  'pigmentationChangeInFace',
  'polycysticOvary',
  'psoriasis',
  'psychiatricConditions',
  'radiotherapy',
  'renalFailure',
  'rheumaticFever',
  'rheumatoidArthritis',
  'rosacea',
  'sarcoidosis',
  'scleroderma',
  'sclerosis',
  'seasonalAllergies',
  'seborrheicDermatitis',
  'seizureDisorder',
  'sensationOfHeatCondition',
  'sexSteroidDeficiency',
  'sjorgenSyndrome',
  'stroke',
  'sunAllergy',
  'thyroid',
  'vitaminDeficiency',
  'vitiligo'
];
const medicalConditionsAutopopulateConfig: {
  [key: string]: FormTemplateFieldAutopopulateConfig[];
} = medicalConditionKeys.reduce((acc, condition) => {
  const config: FormTemplateFieldAutopopulateConfig = {
    incomingDataValuePath: `medicalConditionList.${condition}`,
    mapConditionConfig: {
      comparisonOperator: CompareOperator.equals,
      comparisonValue: true
    },
    transformValueConfig: {
      typeConfig: {
        type: TransformValueTypeEnum.none
      },
      displayFormat: DisplayFormatEnum.none
    }
  };
  acc[condition] = [config];
  return acc;
}, {});

const ocularHistoryKeys = [
  'amblyopia',
  'cataracts',
  'diplopia',
  'dryEyes',
  'eyeTrauma',
  'hyperopia',
  'keratoconus',
  'lasikOrPRK',
  'myopia',
  'nystagmus',
  'orthokeratology',
  'otherPastOcularHistory',
  'pastContactLensDiscomfort',
  'presbyopia',
  'previousEyeInfections',
  'pterygium',
  'retinalConditions',
  'glaucoma',
  'strabismus'
];

const ocularHistoryAutopopulateConfig: {
  [key: string]: FormTemplateFieldAutopopulateConfig[];
} = ocularHistoryKeys.reduce((acc, condition) => {
  const config: FormTemplateFieldAutopopulateConfig = {
    incomingDataValuePath: `ocularHistoryList.${condition}`,
    mapConditionConfig: {
      comparisonOperator: CompareOperator.equals,
      comparisonValue: true
    },
    transformValueConfig: {
      typeConfig: {
        type: TransformValueTypeEnum.none
      },
      displayFormat: DisplayFormatEnum.none
    }
  };
  acc[condition] = [config];
  return acc;
}, {});

const eyeSurgeryKeys = [
  'glaucomaSurgery',
  'cataractSurgery',
  'strabismusSurgery',
  'ptosisSurgery',
  'retinalSurgery',
  'refractiveSurgery',
  'eyelidSurgery',
  'ocularSurfaceSurgery'
];

const eyeSurgeriesAutopopulateConfig: {
  [key: string]: FormTemplateFieldAutopopulateConfig[];
} = eyeSurgeryKeys.reduce((acc, condition) => {
  const config: FormTemplateFieldAutopopulateConfig = {
    incomingDataValuePath: `eyeSurgeriesList.${condition}`,
    mapConditionConfig: {
      comparisonOperator: CompareOperator.equals,
      comparisonValue: true
    },
    transformValueConfig: {
      typeConfig: {
        type: TransformValueTypeEnum.none
      },
      displayFormat: DisplayFormatEnum.none
    }
  };
  acc[condition] = [
    config,
    { ...config, incomingDataValuePath: `eyeSurgeryPerformed-yes-eyeSurgeriesList.${condition}` }
  ];
  return acc;
}, {});

const familyHistoryKeys = [
  'autoImmuneDiseaseHistory',
  'cancerHistory',
  'diabetesHistory',
  'highBloodPressureHistory',
  'highCholestrolHistory',
  'retinalDetachmentHistory',
  'rosaceaHistory'
];

const familyHistoryAutopopulateConfig: {
  [key: string]: FormTemplateFieldAutopopulateConfig[];
} = familyHistoryKeys.reduce((acc, condition) => {
  const config: FormTemplateFieldAutopopulateConfig = {
    incomingDataValuePath: `familyHistoryConditionList.${condition}`,
    mapConditionConfig: {
      comparisonOperator: CompareOperator.equals,
      comparisonValue: true
    },
    transformValueConfig: {
      typeConfig: {
        type: TransformValueTypeEnum.none
      },
      displayFormat: DisplayFormatEnum.none
    }
  };
  acc[condition] = [config];
  return acc;
}, {});

const fitzpatrickKeys = [
  'eyeColor',
  'sunTanExposureFrequency',
  'lastSunExposure',
  'naturalHairColor',
  'skinColor',
  'freckles',
  'longSunExposure',
  'darkerAfterSunExposure',
  'tanIntensity',
  'faceSunSensitivity'
];

const fitzpatrickAutopopulateConfig: {
  [key: string]: FormTemplateFieldAutopopulateConfig[];
} = fitzpatrickKeys.reduce((acc, key) => {
  const config: FormTemplateFieldAutopopulateConfig = {
    incomingDataValuePath: `${key}`,
    mapConditionConfig: {
      comparisonOperator: CompareOperator.notEmpty
    },
    transformValueConfig: {
      typeConfig: {
        type: TransformValueTypeEnum.none
      },
      displayFormat: DisplayFormatEnum.none
    }
  };
  acc[key] = [config];
  return acc;
}, {});

export const nonModifiableAutopopulateConfig: {
  [key: string]: FormTemplateFieldAutopopulateConfig[];
} = {
  ...fitzpatrickAutopopulateConfig,
  styeOrChalazion: [
    {
      incomingDataValuePath: 'styeOrChalazion',
      mapConditionConfig: {
        comparisonOperator: CompareOperator.notEmpty
      },
      transformValueConfig: {
        typeConfig: {
          type: TransformValueTypeEnum.none
        },
        displayFormat: DisplayFormatEnum.none
      }
    }
  ],
  hematopoieticStemCellTransplantation: [
    {
      incomingDataValuePath: 'historyOfHSCT',
      mapConditionConfig: {
        comparisonOperator: CompareOperator.notEmpty
      },
      transformValueConfig: {
        typeConfig: {
          type: TransformValueTypeEnum.none
        },
        displayFormat: DisplayFormatEnum.none
      }
    }
  ],
  ...familyHistoryAutopopulateConfig,
  glaucomaHistory: [
    {
      incomingDataValuePath: 'ocularFamilyHistoryConditionList.glaucomaHistory',
      mapConditionConfig: {
        comparisonOperator: CompareOperator.equals,
        comparisonValue: true
      },
      transformValueConfig: {
        typeConfig: {
          type: TransformValueTypeEnum.none
        },
        displayFormat: DisplayFormatEnum.none
      }
    }
  ],
  cataractsHistory: [
    {
      incomingDataValuePath: 'ocularFamilyHistoryConditionList.cataractsHistory',
      mapConditionConfig: {
        comparisonOperator: CompareOperator.equals,
        comparisonValue: true
      },
      transformValueConfig: {
        typeConfig: {
          type: TransformValueTypeEnum.none
        },
        displayFormat: DisplayFormatEnum.none
      }
    }
  ],
  ...medicalConditionsAutopopulateConfig,
  ...eyeSurgeriesAutopopulateConfig,
  diabetes: [
    ...medicalConditionsAutopopulateConfig.diabetes,
    {
      incomingDataValuePath: `refractiveSurgeryRelatedConditionKeys.diabetes`,
      mapConditionConfig: {
        comparisonOperator: CompareOperator.equals,
        comparisonValue: true
      },
      transformValueConfig: {
        typeConfig: {
          type: TransformValueTypeEnum.none
        },
        displayFormat: DisplayFormatEnum.none
      }
    }
  ],
  ...ocularHistoryAutopopulateConfig,
  female: [
    {
      incomingDataValuePath: 'gender',
      mapConditionConfig: {
        comparisonOperator: CompareOperator.equals,
        comparisonValue: 'female'
      },
      transformValueConfig: {
        typeConfig: {
          type: TransformValueTypeEnum.specifyValue,
          value: true
        },
        displayFormat: DisplayFormatEnum.none
      }
    }
  ],
  male: [
    {
      incomingDataValuePath: 'gender',
      mapConditionConfig: {
        comparisonOperator: CompareOperator.equals,
        comparisonValue: 'male'
      },
      transformValueConfig: {
        typeConfig: {
          type: TransformValueTypeEnum.specifyValue,
          value: true
        },
        displayFormat: DisplayFormatEnum.none
      }
    }
  ],
  childBirth: [
    {
      incomingDataValuePath: 'childBirth',
      mapConditionConfig: {
        comparisonOperator: CompareOperator.equals,
        comparisonValue: 'yes'
      },
      transformValueConfig: {
        typeConfig: {
          type: TransformValueTypeEnum.none
        },
        displayFormat: DisplayFormatEnum.none
      }
    }
  ],
  otherSurgery: [
    {
      incomingDataValuePath: 'eyeSurgeriesList.surgeryOther',
      mapConditionConfig: {
        comparisonOperator: CompareOperator.equals,
        comparisonValue: true
      },
      transformValueConfig: {
        typeConfig: {
          type: TransformValueTypeEnum.none
        },
        displayFormat: DisplayFormatEnum.none
      }
    }
  ],
  otherSurgeryValue: [
    {
      incomingDataValuePath: 'eyeSurgeriesList.surgeryOther-describe',
      mapConditionConfig: {
        comparisonOperator: CompareOperator.notEmpty
      },
      transformValueConfig: {
        typeConfig: {
          type: TransformValueTypeEnum.none
        },
        displayFormat: DisplayFormatEnum.none
      }
    }
  ]
};
